import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MakeApiCallAsync } from '../../../helpers/ApiHelpers';
import Config from '../../../helpers/Config';
import { filterCategoriesWithProducts } from '../../../helpers/CategoryAvailability';
import { getLanguageCodeFromSession } from '../../../helpers/CommonHelper';
import { replaceWhiteSpacesWithDashSymbolInUrl } from '../../../helpers/ConversionHelper';
import CompactCampaignSection from '../../components/shared/CompactCampaignSection';
import './SubCategories.css';

const imageUrl = category => {
    const imageFields = ['attachmenturl', 'pictureurl', 'picturepath', 'categoryimage', 'categoryimageurl', 'imageurl'];
    const imageKey = Object.keys(category || {}).find(key => imageFields.includes(key.toLowerCase().replace(/[^a-z]/g, '')));
    const path = imageKey ? category[imageKey] : null;
    if (!path) return null;
    const normalizedPath = String(path).trim().replace(/\\/g, '/').replace(/^~\//, '').replace(/^\/?wwwroot\//i, '');
    if (normalizedPath.startsWith('data:')) return normalizedPath;
    let url = normalizedPath;
    if (/^\/\//.test(url)) url = `https:${url}`;
    else if (/^https?:\/\//i.test(url)) url = window.location.protocol === 'https:' ? url.replace(/^http:\/\//i, 'https://') : url;
    else {
        const relativePath = url.replace(/^\//, '');
        const fileName = relativePath.split('/').filter(Boolean).pop();
        const resolvedPath = relativePath.toLowerCase().startsWith('content/commonimages/otherimages/')
            ? relativePath
            : `content/commonImages/otherImages/${fileName}`;
        url = `${Config.ADMIN_BASE_URL.replace(/\/$/, '')}/${resolvedPath}`;
    }
    const version = category.ModifiedOn || category.modifiedOn || category.AttachmentID || category.AttachmentId || category.attachmentID;
    return version ? `${url}${url.includes('?') ? '&' : '?'}v=${encodeURIComponent(version)}` : url;
};

const SubCategories = () => {
    const { category_id } = useParams();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [availableChildren, setAvailableChildren] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const langCode = getLanguageCodeFromSession();

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        setAvailableChildren([]);
        const loadCategories = async () => {
            try {
                const response = await MakeApiCallAsync(
                    Config.END_POINT_NAMES.GET_CATEGORIES_LIST,
                    null,
                    { requestParameters: { PageNo: 1, PageSize: 1000, recordValueJson: '[]' } },
                    { Accept: 'application/json', 'Content-Type': 'application/json' },
                    'POST',
                    true
                );
                if (response?.data?.data) {
                    const categoryData = JSON.parse(response.data.data);
                    const children = categoryData.filter(category => Number(category.ParentCategoryID) === Number(category_id));
                    const available = await filterCategoriesWithProducts(children, () => !mounted);
                    if (mounted) {
                        setCategories(categoryData);
                        setAvailableChildren(available);
                    }
                }
            } finally {
                if (mounted) setLoading(false);
            }
        };
        loadCategories().catch(console.error);
        return () => { mounted = false; };
    }, [category_id]);

    const parent = categories.find(category => Number(category.CategoryID) === Number(category_id));
    const visibleChildren = useMemo(
        () => availableChildren.filter(category => String(category.Name || '').toLowerCase().includes(searchTerm.trim().toLowerCase())),
        [availableChildren, searchTerm]
    );
    const productUrl = category => `/${langCode}/all-products/${category.CategoryID}/${replaceWhiteSpacesWithDashSymbolInUrl(category.Name || 'category')}`;

    useEffect(() => {
        setSearchTerm('');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [category_id]);

    return <main style={{ background: '#f5f7fa', minHeight: '55vh', padding: '42px 18px 60px' }}>
        <div style={{ margin: '0 auto', maxWidth: 1200 }}>
            <Link to={`/${langCode}/`} style={{ color: '#2874f0', display: 'inline-block', marginBottom: 18 }}><i className="fas fa-arrow-left" /> Back to home</Link>
            <div style={{ background: 'linear-gradient(135deg, #172337, #2454a6)', borderRadius: 16, color: '#fff', marginBottom: 28, overflow: 'hidden', padding: '30px clamp(20px, 5vw, 48px)', position: 'relative' }}>
                <span style={{ color: '#9fc1ff', fontSize: 12, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase' }}>Browse categories</span>
                <h1 style={{ color: '#fff', fontSize: 30, margin: '7px 0 8px' }}>{parent?.Name || 'Subcategories'}</h1>
                <p style={{ color: '#d8e5ff', margin: '0 0 22px', maxWidth: 620 }}>Select a subcategory to discover products available from sellers serving your selected location.</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                    <label style={{ alignItems: 'center', background: '#fff', borderRadius: 8, display: 'flex', flex: '1 1 300px', maxWidth: 480, padding: '0 14px' }}>
                        <i className="fas fa-search" style={{ color: '#7b8798' }} />
                        <input value={searchTerm} onChange={event => setSearchTerm(event.target.value)} placeholder="Search subcategories" style={{ border: 0, flex: 1, fontSize: 14, outline: 0, padding: '13px 11px' }} />
                        {searchTerm && <button type="button" onClick={() => setSearchTerm('')} aria-label="Clear search" style={{ background: 'none', border: 0, color: '#7b8798', cursor: 'pointer' }}><i className="fas fa-times" /></button>}
                    </label>
                    {parent && <Link to={productUrl(parent)} style={{ alignItems: 'center', background: '#ff9f00', borderRadius: 8, color: '#fff', display: 'inline-flex', fontWeight: 700, padding: '0 20px', textDecoration: 'none' }}>View all products <i className="fas fa-arrow-right" style={{ marginLeft: 9 }} /></Link>}
                </div>
            </div>
            {!loading && <div className="subcategory-results-bar">
                <div>
                    <span>Selected category</span>
                    <strong>{parent?.Name || 'All products'}</strong>
                    <small>{visibleChildren.length} subcategor{visibleChildren.length === 1 ? 'y' : 'ies'} available</small>
                </div>
                {parent && <Link className="subcategory-results-bar__button" to={productUrl(parent)}>
                    <span><i className="fas fa-box-open" aria-hidden="true" /></span>
                    <span><small>Skip subcategories</small><strong>View all {parent.Name} products</strong></span>
                    <i className="fas fa-arrow-right" aria-hidden="true" />
                </Link>}
            </div>}
            {loading ? <p>Loading subcategories...</p> : visibleChildren.length ?
                <div style={{ display: 'grid', gap: 18, gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))' }}>
                    {visibleChildren.map(category => <Link key={category.CategoryID} to={productUrl(category)} style={{ alignItems: 'center', background: '#fff', border: '1px solid #e5e9ef', borderRadius: 18, boxShadow: '0 5px 18px rgba(23,35,55,.06)', color: '#172337', display: 'flex', flexDirection: 'column', minHeight: 210, padding: 20, textAlign: 'center', textDecoration: 'none' }}>
                        {imageUrl(category) ? <img src={imageUrl(category)} alt="" style={{ height: 105, marginBottom: 14, objectFit: 'contain', width: '100%' }} /> : <span style={{ alignItems: 'center', background: '#eef4ff', borderRadius: '50%', color: '#2874f0', display: 'flex', fontSize: 30, height: 90, justifyContent: 'center', marginBottom: 14, width: 90 }}><i className="fas fa-th-large" /></span>}
                        <strong>{category.Name}</strong>
                        <span style={{ color: '#2874f0', fontSize: 12, marginTop: 9 }}>Explore products <i className="fas fa-chevron-right" /></span>
                    </Link>)}
                </div>
                : <div style={{ background: '#fff', borderRadius: 10, padding: 28 }}>
                    <p>{searchTerm ? `No subcategories match “${searchTerm}”.` : 'No subcategories with available products were found.'}</p>
                    {searchTerm && <button type="button" onClick={() => setSearchTerm('')} style={{ background: 'none', border: 0, color: '#2874f0', cursor: 'pointer', padding: 0 }}>Clear search</button>}
                    {parent && <Link to={productUrl(parent)}>View {parent.Name} products</Link>}
                </div>}
            <div style={{ background: '#fff', border: '1px solid #e5e9ef', borderRadius: 12, display: 'grid', gap: 20, gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', marginTop: 34, padding: 24 }}>
                {[['fa-map-marker-alt', 'Local availability', 'Results respect your selected delivery PIN.'], ['fa-store', 'Trusted sellers', 'Browse manufacturer, reseller and hardware-shop products.'], ['fa-headset', 'Need help?', 'Contact us if you cannot find the machinery you need.']].map(item => <div key={item[1]} style={{ alignItems: 'flex-start', display: 'flex', gap: 13 }}><span style={{ color: '#2874f0', fontSize: 20, width: 25 }}><i className={`fas ${item[0]}`} /></span><div><strong style={{ color: '#172337', display: 'block', marginBottom: 4 }}>{item[1]}</strong><small style={{ color: '#7b8798', lineHeight: 1.5 }}>{item[2]}</small></div></div>)}
            </div>
            <div style={{ margin: '28px -12px 0' }}>
                <CompactCampaignSection />
            </div>
        </div>
    </main>;
};

export default SubCategories;
